const publicSolanaRpcHosts = new Set([
  "api.devnet.solana.com",
  "api.testnet.solana.com",
  "api.mainnet-beta.solana.com"
]);

export function normalizePrivySolanaNetwork(cluster) {
  const value = String(cluster || "").trim().toLowerCase();
  const networkId = value === "mainnet-beta" ? "mainnet" : value;
  if (!["devnet", "testnet", "mainnet"].includes(networkId)) {
    return { networkId: "", solanaChain: "" };
  }
  return {
    networkId,
    solanaChain: `solana:${networkId}`
  };
}

export function normalizePrivyCreateWalletOnLogin(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "1" || normalized === "true" ? "users-without-wallets" : "off";
}

export function getPrivyEmbeddedConfig(env = import.meta.env, runtime = {}) {
  const appId = String(env.VITE_PRIVY_APP_ID || "").trim();
  const { networkId, solanaChain } = normalizePrivySolanaNetwork(env.VITE_SOLANA_CLUSTER || "mainnet-beta");
  const hasPrivyRpcOverride = Boolean(String(env.VITE_PRIVY_SOLANA_RPC_URL || "").trim());
  const rpcUrl = resolvePrivySolanaRpcUrl(env, runtime);
  const rpcSubscriptionsUrl = resolvePrivySolanaRpcSubscriptionsUrl(env, {
    hasPrivyRpcOverride,
    rpcUrl,
    usingProxy: isPrivySolanaRpcProxyUrl(rpcUrl, runtime)
  });
  const enabled =
    env.VITE_DISABLE_API !== "1" &&
    env.VITE_PRIVY_EMBEDDED_ENABLED === "1" &&
    Boolean(appId) &&
    Boolean(networkId) &&
    Boolean(rpcUrl);

  return {
    enabled,
    appId,
    networkId,
    solanaChain,
    rpcUrl,
    rpcSubscriptionsUrl,
    createWalletOnLogin: normalizePrivyCreateWalletOnLogin(env.VITE_PRIVY_CREATE_WALLET_ON_LOGIN)
  };
}

function resolvePrivySolanaRpcUrl(env, runtime = {}) {
  const proxyUrl = resolvePrivySolanaRpcProxyUrl(env, runtime);
  if (proxyUrl) return proxyUrl;
  const rpcUrl = String(env.VITE_PRIVY_SOLANA_RPC_URL || env.VITE_SOLANA_RPC_URL || "").trim();
  return isPublicSolanaRpcUrl(rpcUrl) ? "" : rpcUrl;
}

function resolvePrivySolanaRpcSubscriptionsUrl(env, { hasPrivyRpcOverride, rpcUrl, usingProxy = false }) {
  if (!rpcUrl) return "";
  if (usingProxy) return "";
  const configuredWsUrl = String(
    env.VITE_PRIVY_SOLANA_WS_URL ||
      (!hasPrivyRpcOverride ? env.VITE_SOLANA_WS_URL : "") ||
      ""
  ).trim();
  if (configuredWsUrl && !isPublicSolanaRpcUrl(configuredWsUrl)) {
    return configuredWsUrl;
  }
  return deriveWebsocketEndpoint(rpcUrl);
}

function resolvePrivySolanaRpcProxyUrl(env, runtime = {}) {
  if (env.VITE_PRIVY_SOLANA_RPC_PROXY_ENABLED === "0") return "";
  const origin = readRuntimeOrigin(runtime);
  return origin ? `${origin}/api/privy/solana-rpc` : "";
}

function isPrivySolanaRpcProxyUrl(rpcUrl, runtime = {}) {
  const origin = readRuntimeOrigin(runtime);
  return Boolean(origin && rpcUrl === `${origin}/api/privy/solana-rpc`);
}

function readRuntimeOrigin(runtime = {}) {
  const origin =
    runtime.location?.origin ||
    (typeof window !== "undefined" ? window.location?.origin : "") ||
    "";
  return String(origin).trim().replace(/\/+$/, "");
}

function isPublicSolanaRpcUrl(endpoint) {
  if (!endpoint) return false;
  try {
    return publicSolanaRpcHosts.has(new URL(endpoint).hostname.toLowerCase());
  } catch {
    return false;
  }
}

function deriveWebsocketEndpoint(endpoint) {
  if (!endpoint) return "";
  try {
    const url = new URL(endpoint);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    return url.toString();
  } catch {
    return "";
  }
}
